import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  MotorLab,
  MotorLossMap,
  MotorTorqueCurves,
  UnitType,
} from '../../../../../../api';
import { ConceptInputComponent } from '../../../forms/input/concept-input.component';
import {
  LabelPosition,
  SwitchSize,
  InputType, InputSize,
} from '@ansys-internal/awc-angular/forms';
import { SetPart } from '../mass/mass.component';
import { ComponentWidth } from '@ansys-internal/awc-angular/core';
import { Icons } from '@ansys-internal/awc-angular/icons';
import { UploadFileResponseType } from '../../../../../shared/types/upload-file-response.type';
import { FeatureFlagsEnum } from '../../../../../shared/enums/feature-flags.enum';
import { AWCListItem } from '@ansys-internal/awc-angular/lists';
import { defaultPartsMap } from '../../../../dialogs/add-part/lib/default-parts-map';
import { PartType } from '../../../../../shared/enums';
import { FeatureFlagModule } from '../../../../../shared/modules/feature-flag.module';
import { CommonModule } from '@angular/common';
import { TooltipsModule } from '@ansys-internal/awc-angular/tooltips';
import {FormSelectComponent} from "@ansys-internal/awc-angular/form-select";
import {AWCFormElementSize} from "@ansys-internal/awc-angular/form";
import {SharedModule} from "../../../../../shared/shared.module";
import { ComponentsService } from "../../../../../shared/services/components.service";
import {FormInputComponent} from "@ansys-internal/awc-angular/form-input";
import { AccordionComponent } from '@ansys-internal/awc-angular/accordions';

@Component({
    selector: 'app-motor-inputs',
    templateUrl: './motor.component.html',
    styleUrls: ['../inputs.scss'],
    imports: [
      ConceptInputComponent,
      CommonModule,
      FeatureFlagModule,
      ConceptInputComponent,
      TooltipsModule,
      FormSelectComponent,
      SharedModule,
      AccordionComponent,
    ]
})
export class MotorComponent {
  @Input() part!: MotorLossMap | MotorLab | MotorTorqueCurves | any;
  @Input() thermalFile?: File;
  @Output() thermalFileChange: EventEmitter<File> = new EventEmitter<File>();
  @Input() update!: boolean;
  @Input() readonly!: boolean;
  @Input() public fileResponse?: UploadFileResponseType;
  @Input() public forceEnable!: boolean;
  @Output() setPart: EventEmitter<SetPart> = new EventEmitter<SetPart>();
  @Output() forceUpdate: EventEmitter<string> = new EventEmitter<string>();
  @Output() public partChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() public updateInput: EventEmitter<string> =
    new EventEmitter<string>();
  protected UnitType = UnitType;
  protected InputTypes = InputType;
  protected size: SwitchSize = SwitchSize.MEDIUM;
  protected labelPosition: LabelPosition = LabelPosition.BEFORE;
  protected awcSizing: ComponentWidth = ComponentWidth.FILL;
  protected awcTooltip: string = 'Include Losses';
  protected readonly Icons = Icons;
  protected readonly featureFlagsEnum = FeatureFlagsEnum;
  protected controlStrategyBpms: AWCListItem[] = [];
  protected controlStrategyBpm?: string;
  protected controlStrategyBpmKey?: number = 0;
  protected controlStrategySyncs: AWCListItem[] = [];
  protected statorLimitTypes: AWCListItem[] = [];
  protected controlStrategySync?: AWCListItem;
  protected controlStrategySyncKey?: number = 0;
  @ViewChild('fileUpload') upload!: ElementRef;
  protected file?: File;
  public title: string | undefined = '';
  protected readonly FeatureFlagsEnum = FeatureFlagsEnum;
  protected readonly Math = Math;
  protected readonly AWCFormElementSize = AWCFormElementSize;

  constructor(private cdr: ChangeDetectorRef, private componentService: ComponentsService) {}

  ngOnInit(): void {
    this.initDefaultDropdownValues();
    this.setDropdownValues();
  }

  ngOnChanges(changes: any): void {
    if (changes.fileResponse && this.fileResponse?.responseObject) {
      this.part = {
        ...this.part,
        ...this.fileResponse?.responseObject,
      };

      // Emit the file response to the parent component
      for (const [key, value] of Object.entries(
        this.fileResponse?.responseObject
      )) {
        this.setPart.emit([value, key, `${this.part.id}_${key}`]);
      }
    }
  }

  get minSpeed(): number {
    const speeds = this.part?.thermal_model_details?.speeds ?? [];
    return speeds.length ? Math.min(...speeds) : 0;
  }

  get maxSpeed(): number {
    const speeds = this.part?.thermal_model_details?.speeds ?? [];
    return speeds.length ? Math.max(...speeds) : 0;
  }

  get minFlowRate(): number {
    const rates = this.part?.thermal_model_details?.flow_rates ?? [];
    return rates.length ? Math.min(...rates) : 0;
  }

  get maxFlowRate(): number {
    const rates = this.part?.thermal_model_details?.flow_rates ?? [];
    return rates.length ? Math.max(...rates) : 0;
  }

  private getAveFlowRate(flow_rates: number[]): number {
    if (!flow_rates || flow_rates.length === 0) {
      return 0;
    }
    const minFlowRate = Math.min(...flow_rates);
    const maxFlowRate = Math.max(...flow_rates);
    return (minFlowRate + maxFlowRate) / 2;
  }

  protected onControlStrategyChange($event: any, partKey: string): void {
    this.setPart.emit([$event, partKey, this.part.id + '_' + partKey]);
  }

  protected onStatorLimitTypeChange($event: any, partKey: string): void {
    this.setPart.emit([$event, partKey, this.part.id + '_' + partKey]);
  }

  private initDefaultDropdownValues(): void {
    const mapToAWCListItems = (data: Record<string, number|string>): AWCListItem[] =>
      Object.entries(data).map(([text, id]) => ({ id: id.toString(), text }));

    const motorLabDropdowns = defaultPartsMap.get(
      PartType.MOTOR_LAB_DROPDOWNS
    ) as
      | { BPM: Record<string, number>; SYNC: Record<string, number> }
      | undefined;

    if (motorLabDropdowns) {
      this.controlStrategyBpms = mapToAWCListItems(motorLabDropdowns.BPM);
      this.controlStrategySyncs = mapToAWCListItems(motorLabDropdowns.SYNC);
      this.statorLimitTypes = mapToAWCListItems({
        Average: "average",
        Hotspot: "hotspot",
      });
    }
  }

  private setDropdownValues(): void {
    this.controlStrategyBpmKey = this.part.rolling_resistance_key || 0;
    this.controlStrategyBpm = this.part.control_strategy_bpm || 0;

    this.controlStrategySyncKey = this.part.control_strategy_sync || 0;
    this.controlStrategySync = this.part.control_strategy_sync || 0;
  }

  protected onUploadClicked(): void {
    this.upload.nativeElement.click();
  }

  protected async onFileSelected(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;

    const file = target.files?.[0];
    if (file) {
      this.componentService.uploadState$.next(true);
      this.thermalFile = file;
      const itemId = this.fileResponse?.id ?? this.part.data_id;
      try {
        const thermalFileResponse = await this.componentService.addThermalModelFile(
          itemId as string,
          file
        );

        this.part.thermal_model_details = thermalFileResponse;
        this.part.flow_rate = this.getAveFlowRate(thermalFileResponse.flow_rates);

        if (!this.update) {
          this.setPart.emit([this.part.flow_rate + '', 'flow_rate', this.part.id + '_flow_rate']);

          this.thermalFileChange.emit(this.thermalFile);
        }
      } catch (e) {}

      this.componentService.uploadState$.next(false);
    }

    this.cdr.detectChanges();
  }

  protected readonly InputSize = InputSize;
}
